import React from 'react';
import {
    ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import { Payments, Store, Savings } from '@mui/icons-material';
import StatCard from './StatCard';
import ChartCard from './ChartCard';
import TableCard from './TableCard';
import ChartTooltip from './ChartTooltip';
import { COLORS } from '../shared/tokens';
import { gbp, gbpCompact, shortWeek, SERIES, CATEGORY_PALETTE } from './format';
import { formatDisplayWeek } from '../../../helper';
import { formatDisplayDate } from '../../../helper';

export default function AccountDashboard({ data }) {
    const { kpis, trend = [], topShops = [], recent = [] } = data;
    const spark = trend.map((t) => ({ v: t.amt }));

    return (
        <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <StatCard label="Total Paid" value={gbpCompact(kpis.paidTotal)} sub="all time" accent={COLORS.primary} icon={<Payments fontSize="small" />} />
                <StatCard label="8-Week Paid" value={gbpCompact(kpis.paid8)} sub={`through ${kpis.latestWeekCode}`} accent="#2563eb" icon={<Payments fontSize="small" />} spark={spark} />
                <StatCard label="Latest Week" value={gbpCompact(kpis.latestWeek)} sub={kpis.latestWeekCode} accent="#a16207" icon={<Payments fontSize="small" />} />
                <StatCard label="Shops Linked" value={kpis.shopsLinked} accent="#15803d" icon={<Store fontSize="small" />} />
                <StatCard label="Opening Balance" value={gbp(kpis.openingBalance)} accent="#7c3aed" icon={<Savings fontSize="small" />} />
            </div>

            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 16 }}>
                <ChartCard title="Weekly Payments" subtitle="from shops" flex="2 1 460px" legend={[{ label: 'Paid', color: SERIES.card }]}>
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trend} margin={{ top: 6, right: 8, left: -12, bottom: 0 }} barSize={22}>
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} vertical={false} />
                            <XAxis dataKey="week" tickFormatter={shortWeek} tick={{ fill: COLORS.textDim, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tickFormatter={(v) => gbpCompact(v)} tick={{ fill: COLORS.textDim, fontSize: 11 }} axisLine={false} tickLine={false} width={54} />
                            <Tooltip cursor={{ fill: COLORS.primaryTintSoft }} content={<ChartTooltip week />} />
                            <Bar dataKey="amt" name="Paid" fill={SERIES.card} radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>

                <ChartCard title="Top Shops" subtitle="by amount paid" flex="1 1 320px">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart layout="vertical" data={topShops} margin={{ top: 0, right: 12, left: 8, bottom: 0 }} barSize={14}>
                            <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} horizontal={false} />
                            <XAxis type="number" tickFormatter={(v) => gbpCompact(v)} tick={{ fill: COLORS.textDim, fontSize: 10 }} axisLine={false} tickLine={false} />
                            <YAxis type="category" dataKey="name" width={100} tick={{ fill: COLORS.text, fontSize: 11 }} axisLine={false} tickLine={false} />
                            <Tooltip cursor={{ fill: COLORS.primaryTintSoft }} content={<ChartTooltip />} />
                            <Bar dataKey="total" radius={[0, 4, 4, 0]}>
                                {topShops.map((_, i) => <Cell key={i} fill={CATEGORY_PALETTE[i % CATEGORY_PALETTE.length]} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </ChartCard>
            </div>

            <TableCard
                title="Recent Payment Entries"
                columns={[
                    { key: 'week', label: 'Week', render: (r) => formatDisplayWeek(r.week) },   // "10 Aug 2026 - 16 Aug 2026", as everywhere else
                    { key: 'source', label: 'From', render: (r) => r.source || 'N/A' },
                    { key: 'fromType', label: 'Type' },
                    { key: 'detail', label: 'Detail', render: (r) => r.detail || '—' },
                    { key: 'date', label: 'Date', render: (r) => formatDisplayDate(r.date) || r.date },
                    { key: 'amount', label: 'Amount', align: 'right', render: (r) => gbp(r.amount) },
                ]}
                rows={recent}
                emptyLabel="No payment entries recorded yet"
            />
        </>
    );
}
